// PublicOrderService.js
import { getSupabaseClient } from "@shared/core/services/SupabaseService";

/**
 * Public Order Service for customer-facing order operations
 * This service handles order retrieval for customers (not admin operations)
 * Note: No client-side decryption - returns data as-is for customer display
 */
class PublicOrderService {
  constructor() {
    this.tableName = "orders";
    this.supabase = getSupabaseClient();
  }

  /**
   * Get order details by Stripe session ID
   * Used for payment confirmation pages
   */
  async getOrderBySessionId(sessionId, websiteId) {
    try {
      if (!sessionId || !websiteId) {
        throw new Error("Session ID and Website ID are required");
      }

      const { data, error } = await this.supabase
        .from(this.tableName)
        .select(
          `
          id,
          order_reference,
          customer_email,
          subtotal_amount,
          shipping_amount,
          vat_amount,
          total_amount,
          currency,
          status,
          created_at,
          shipping_address,
          items:order_items(
            id,
            product_id,
            quantity,
            unit_price,
            total_price,
            is_digital,
            digital_metadata,
            products(name)
          )
        `,
        )
        .eq("stripe_session_id", sessionId)
        .eq("website_id", websiteId)
        .eq("status", "paid")
        .single();

      if (error) {
        console.error("Error fetching order by session ID:", {
          errorCode: error.code,
          errorMessage: error.message,
        });
        throw new Error("Order not found");
      }

      // Return order data as-is (no client-side decryption)
      return data;
    } catch (error) {
      console.error("Error in getOrderBySessionId:", {
        errorMessage: error.message,
      });
      throw error;
    }
  }

  /**
   * Get order details by Stripe payment intent ID
   * Used for payment confirmation pages after Stripe redirect
   * Uses database function to bypass RLS issues with nested queries
   */
  async getOrderByPaymentIntentId(paymentIntentId, websiteId) {
    try {
      if (!paymentIntentId || !websiteId) {
        throw new Error("Payment Intent ID and Website ID are required");
      }

      // Use database function to bypass RLS issues with nested queries
      const { data, error } = await this.supabase.rpc("get_order_with_items", {
        p_payment_intent: paymentIntentId,
        p_website_id: websiteId,
      });

      if (error) {
        // Log comprehensive error details for debugging
        console.error("Error fetching order by payment intent:", {
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: error.details,
          errorHint: error.hint,
          paymentIntentId,
          websiteId,
          timestamp: new Date().toISOString(),
        });

        // Provide more specific error messages for debugging
        if (error.code === "PGRST116" || error.code === "PGRST204") {
          throw new Error(
            "Order not yet created - payment may still be processing",
          );
        } else if (error.code === "42501") {
          throw new Error("Database permission error - contact support");
        } else if (error.message?.includes("JWT")) {
          throw new Error(
            "Authentication error - please refresh and try again",
          );
        } else {
          throw new Error(
            `Database error [${error.code || "UNKNOWN"}]: ${error.message || "Unknown error"}`,
          );
        }
      }

      if (!data) {
        console.error("No order data returned (but no error):", {
          paymentIntentId,
          websiteId,
          timestamp: new Date().toISOString(),
        });
        throw new Error(
          "Order not yet created - payment may still be processing",
        );
      }

      // Return order data as-is (no client-side decryption)
      return data;
    } catch (error) {
      console.error("Error in getOrderByPaymentIntentId:", {
        errorMessage: error.message,
        errorName: error.name,
        paymentIntentId,
        websiteId,
        timestamp: new Date().toISOString(),
      });

      // Re-throw with context for better debugging
      throw error;
    }
  }

  /**
   * Get order details by order reference
   * Used for payment confirmation from navigation state
   */
  async getOrderByReference(orderReference, websiteId) {
    try {
      if (!orderReference || !websiteId) {
        throw new Error("Order Reference and Website ID are required");
      }

      const { data, error } = await this.supabase
        .from(this.tableName)
        .select(
          `
          id,
          order_reference,
          customer_email,
          subtotal_amount,
          shipping_amount,
          vat_amount,
          total_amount,
          currency,
          status,
          created_at,
          shipping_address,
          items:order_items(
            id,
            product_id,
            quantity,
            unit_price,
            total_price,
            is_digital,
            digital_metadata,
            products(name)
          )
        `,
        )
        .eq("order_reference", orderReference)
        .eq("website_id", websiteId)
        .eq("status", "paid")
        .single();

      if (error) {
        console.error("Error fetching order by reference:", {
          errorCode: error.code,
          errorMessage: error.message,
        });
        throw new Error("Order not found");
      }

      // Return order data as-is (no client-side decryption)
      return data;
    } catch (error) {
      console.error("Error in getOrderByReference:", {
        errorMessage: error.message,
      });
      throw error;
    }
  }

  /**
   * Get order details by order number for customer lookup
   */
  async getOrderByNumber(orderNumber, customerEmail, websiteId) {
    try {
      if (!orderNumber || !customerEmail || !websiteId) {
        throw new Error(
          "Order number, customer email, and Website ID are required",
        );
      }

      const { data, error } = await this.supabase
        .from(this.tableName)
        .select(
          `
          id,
          order_number,
          customer_email,
          customer_name,
          subtotal,
          shipping_amount,
          total_amount,
          currency,
          status,
          created_at,
          shipping_address,
          items:order_items(
            id,
            product_id,
            product_name,
            quantity,
            price,
            is_digital,
            digital_metadata
          )
        `,
        )
        .eq("order_number", orderNumber)
        .eq(
          "customer_email_hash",
          this.encryptionService.hashForSearch(customerEmail),
        )
        .eq("website_id", websiteId)
        .single();

      if (error) {
        console.error("Error fetching order by number:", {
          errorCode: error.code,
          errorMessage: error.message,
        });
        throw new Error("Order not found");
      }

      // Return order data as-is (no client-side decryption)
      return data;
    } catch (error) {
      console.error("Error in getOrderByNumber:", {
        errorMessage: error.message,
      });
      throw error;
    }
  }
  /**
   * Get decrypted customer data for customer-facing order display
   * Note: This is intended for customer use, not admin operations
   */
  async getDecryptedCustomerData(orderData, customerEmail) {
    try {
      // Validate that the provided customer email matches the order
      // This is a security check to ensure customers can only decrypt their own data
      if (!customerEmail) {
        console.warn("Customer email required for customer data decryption");
        return this.getFallbackCustomerData(orderData);
      }

      // For customer-facing operations, we can use the customer authentication
      // or fall back to showing appropriate messaging for encrypted data
      const decryptedData = {
        customer_email: null,
        shipping_address: null,
        is_digital_only: false,
      };

      // Try to determine if this is a digital-only order
      const hasShippingData =
        orderData.shipping_address &&
        typeof orderData.shipping_address === "object" &&
        Object.keys(orderData.shipping_address).length > 0 &&
        orderData.shipping_address !== "{}";

      decryptedData.is_digital_only = !hasShippingData;

      // For customer-facing display, we can show the email they used for the order
      // (since they already know their own email)
      decryptedData.customer_email = customerEmail;

      // For shipping address, we need to handle different scenarios
      if (decryptedData.is_digital_only) {
        decryptedData.shipping_address = {
          type: "digital_delivery",
          message: "This is a digital order - no shipping required",
          delivery_method: "Email download links",
        };
      } else if (orderData.shipping_address_encrypted) {
        // For encrypted shipping data, we show a privacy-compliant message
        decryptedData.shipping_address = {
          type: "encrypted",
          message: "Shipping address is encrypted for your privacy",
          delivery_method: "Physical shipping",
        };
      } else if (orderData.shipping_address) {
        // Use existing plaintext shipping address (during migration period)
        decryptedData.shipping_address = orderData.shipping_address;
      } else {
        // Fallback for orders without shipping data
        decryptedData.shipping_address = {
          type: "unknown",
          message: "Shipping information not available",
          delivery_method: "Contact support for details",
        };
      }

      return decryptedData;
    } catch (error) {
      console.error("Error getting decrypted customer data:", {
        error: error.message,
        orderReference: orderData?.order_reference,
      });

      return this.getFallbackCustomerData(orderData);
    }
  }

  /**
   * Fallback customer data for when decryption is not available
   */
  getFallbackCustomerData(orderData) {
    const hasShippingData =
      orderData?.shipping_address &&
      typeof orderData.shipping_address === "object" &&
      Object.keys(orderData.shipping_address).length > 0 &&
      orderData.shipping_address !== "{}";

    return {
      customer_email: null,
      shipping_address: hasShippingData
        ? orderData.shipping_address
        : {
            type: "digital_delivery",
            message: "Digital order - no shipping required",
            delivery_method: "Email download links",
          },
      is_digital_only: !hasShippingData,
      encryption_status: "fallback",
    };
  }

  /**
   * Enhanced order lookup with customer-appropriate data display
   */
  async getOrderBySessionIdWithCustomerData(sessionId, websiteId) {
    try {
      const order = await this.getOrderBySessionId(sessionId, websiteId);
      const decryptedData = await this.getDecryptedCustomerData(
        order,
        order.customer_email,
      );

      return {
        ...order,
        customer_data: decryptedData,
      };
    } catch (error) {
      console.error("Error in getOrderBySessionIdWithCustomerData:", {
        sessionId,
        websiteId,
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * Enhanced order lookup by payment intent with customer data
   */
  async getOrderByPaymentIntentIdWithCustomerData(paymentIntentId, websiteId) {
    try {
      const order = await this.getOrderByPaymentIntentId(
        paymentIntentId,
        websiteId,
      );
      const decryptedData = await this.getDecryptedCustomerData(
        order,
        order.customer_email,
      );

      return {
        ...order,
        customer_data: decryptedData,
      };
    } catch (error) {
      console.error("Error in getOrderByPaymentIntentIdWithCustomerData:", {
        paymentIntentId,
        websiteId,
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * Enhanced order lookup by reference with customer data
   */
  async getOrderByReferenceWithCustomerData(orderReference, websiteId) {
    try {
      const order = await this.getOrderByReference(orderReference, websiteId);
      const decryptedData = await this.getDecryptedCustomerData(
        order,
        order.customer_email,
      );

      return {
        ...order,
        customer_data: decryptedData,
      };
    } catch (error) {
      console.error("Error in getOrderByReferenceWithCustomerData:", {
        orderReference,
        websiteId,
        error: error.message,
      });
      throw error;
    }
  }
}

export const publicOrderService = new PublicOrderService();
